/**
 * Login Page
 * 
 * User authentication page with email/password and social login.
 * 
 * @module pages/auth/LoginPage
 */

import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { usePageTitle } from '@/hooks';
import { Button, Input, Alert } from '@/components/ui';
import { SocialAuthButtons } from '@/components/auth';
import { ROLE_LABELS, type OrgRole } from '@/lib/firebase-types';
import { Eye, EyeOff, Mail } from 'lucide-react';

// =============================================================================
// Types
// =============================================================================

interface LocationState {
  from?: { pathname?: string; search?: string };
  loggedOut?: boolean;
  isInvite?: boolean;
  inviteDetails?: {
    email?: string;
    organizationName?: string;
    inviterName?: string;
    role?: string;
  };
}

// =============================================================================
// Component
// =============================================================================

export function LoginPage() {
  usePageTitle('Sign In');

  const navigate = useNavigate();
  const location = useLocation();
  const { signIn, error, clearError } = useAuth();

  // Get invite details and redirect destination from location state
  const locationState = location.state as LocationState | null;
  const fromState = locationState?.from;
  const from = fromState ? `${fromState.pathname || '/overview'}${fromState.search || ''}` : '/overview';
  const justLoggedOut = locationState?.loggedOut === true;
  const isInvite = locationState?.isInvite === true;
  const inviteDetails = locationState?.inviteDetails;

  const [email, setEmail] = useState(inviteDetails?.email || '');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  // Clear any existing auth errors on mount
  useEffect(() => {
    clearError();
  }, [clearError]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      await signIn(email, password);
      navigate(from, { replace: true });
    } catch {
      // Error handled in AuthContext
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex overflow-auto no-scrollbar">
      {/* Left side - Form */}
      <div className="flex-1 flex flex-col justify-center px-4 sm:px-6 lg:px-8 py-12">
        <div className="w-full max-w-sm mx-auto">
          {/* Logo */}
          <div className="flex items-center gap-2 mb-8">
            <div className="flex items-center justify-center h-10 w-10 rounded-xl bg-brand-500 overflow-hidden">
              <svg className="w-10 h-10" viewBox="0 0 32 32" fill="none">
                <path d="M7 10L13 16L7 22" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" opacity="0.5"/>
                <path d="M13 10L19 16L13 22" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" opacity="0.75"/>
                <path d="M19 10L25 16L19 22" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <span className="font-display font-semibold text-xl text-gray-900">
              Hummingbird
            </span>
          </div>

          {/* Header */}
          <div className="mb-8">
            <h1 className="text-2xl font-display font-semibold text-gray-900">
              {isInvite ? 'Sign in to accept' : 'Sign in to your account'}
            </h1>
            <p className="text-gray-500 mt-1">
              {isInvite
                ? 'Join your team today on Hummingbird.'
                : 'Build something amazing today.'}
            </p>
          </div>

          {/* Invite Banner */}
          {isInvite && (
            <div className="mb-6 p-4 bg-brand-50 border border-brand-200 rounded-lg flex items-start gap-3">
              <Mail className="h-5 w-5 text-brand-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm">
                <p className="font-medium text-brand-900">You've been invited!</p>
                <p className="text-brand-700 mt-0.5">
                  {inviteDetails?.inviterName && inviteDetails?.organizationName ? (
                    <>
                      <span className="font-medium">{inviteDetails.inviterName}</span> has invited you to join{' '}
                      <span className="font-medium">{inviteDetails.organizationName}</span>
                      {inviteDetails.role && <> as {inviteDetails.role === 'admin' || inviteDetails.role === 'org_admin' ? 'an' : 'a'} <span className="font-medium">{ROLE_LABELS[inviteDetails.role as OrgRole] || inviteDetails.role}</span></>}.
                    </>
                  ) : (
                    'Sign in to accept your team invitation.'
                  )}
                </p>
              </div>
            </div>
          )}

          {/* Logout Success Alert */}
          {justLoggedOut && (
            <Alert variant="success" className="mb-6">
              You have been signed out successfully.
            </Alert>
          )}

          {/* Error Alert */}
          {error && (
            <Alert variant="error" className="mb-6" onDismiss={clearError}>
              {error}
            </Alert>
          )}

          {/* Social Auth */}
          <SocialAuthButtons mode="signin" />

          {/* Divider */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-200" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500">or</span>
            </div>
          </div>

          {/* Email Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <Input
              label="Email"
              type="email"
              name="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              autoComplete="email"
              autoFocus
              required
            />

            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label
                  htmlFor="password"
                  className="block text-sm font-medium text-gray-700"
                >
                  Password
                </label>
                <Link
                  to="/forgot-password"
                  className="text-sm text-brand-600 hover:text-brand-700"
                  tabIndex={-1}
                >
                  Forgot?
                </Link>
              </div>
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                name="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                autoComplete="current-password"
                required
                rightElement={
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="text-gray-400 hover:text-gray-600"
                    tabIndex={-1}
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5" />
                    ) : (
                      <Eye className="h-5 w-5" />
                    )}
                  </button>
                }
              />
            </div>

            <Button type="submit" fullWidth loading={loading}>
              Sign in
            </Button>

            <div className="text-center">
              <Link
                to="/forgot-password"
                className="text-sm text-brand-600 hover:text-brand-700"
              >
                Forgot your password?
              </Link>
            </div>
          </form>

          {/* Sign up link */}
          <p className="text-center text-sm text-gray-500 mt-6">
            Don't have an account?{' '}
            <Link
              to="/signup"
              state={location.state}
              className="font-medium text-brand-600 hover:text-brand-700"
            >
              Sign up
            </Link>
          </p>
        </div>
      </div>

      {/* Right side - Decorative */}
      <div className="hidden lg:flex lg:flex-1 bg-gradient-to-br from-brand-500 to-brand-600 relative overflow-hidden">
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-10">
          <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                <path d="M 40 0 L 0 0 0 40" fill="none" stroke="white" strokeWidth="1"/>
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid)" />
          </svg>
        </div>

        {/* Content */}
        <div className="relative flex flex-col justify-center px-12 text-white">
          <h2 className="text-4xl font-display font-semibold mb-4">
            Engineered to an unreasonable standard.
          </h2>
          <p className="text-xl text-white/80 max-w-md">
            We spent years shaving off milliseconds that other platforms ignore. You get the benefit.
          </p>

          {/* Stats */}
          <div className="flex gap-12 mt-12">
            <div>
              <p className="text-4xl font-semibold">&lt;50ms</p>
              <p className="text-white/60">Median latency</p>
            </div>
            <div>
              <p className="text-4xl font-semibold">99.99%</p>
              <p className="text-white/60">Uptime SLA</p>
            </div>
            <div>
              <p className="text-4xl font-semibold">300+</p>
              <p className="text-white/60">Edge locations</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
